import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

// Helper to get stored token
const getStoredToken = () => localStorage.getItem('access_token');
const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem('access_token', token);
  } else {
    localStorage.removeItem('access_token');
  }
};

// Configure axios to send token in header
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error.message);
      setUser(null);
      setStoredToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, 
      { email, password }
    );
    setUser(response.data.user);
    setStoredToken(response.data.access_token);
    return response.data;
  }, []);

  const register = useCallback(async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    setUser(response.data.user);
    setStoredToken(response.data.access_token);
    return response.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {});
    } catch (e) {
      console.error('Logout error:', e.message);
    }
    setUser(null);
    setStoredToken(null);
  }, []);

  const loginWithGoogle = useCallback(() => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const handleGoogleCallback = useCallback(async (sessionId) => {
    const response = await axios.post(`${API_URL}/api/auth/google/session`,
      { session_id: sessionId }
    );
    setUser(response.data.user);
    setStoredToken(response.data.access_token);
    return response.data;
  }, []);

  const value = useMemo(() => ({ 
    user, 
    loading, 
    login, 
    register, 
    logout, 
    loginWithGoogle,
    handleGoogleCallback,
    checkAuth
  }), [user, loading, login, register, logout, loginWithGoogle, handleGoogleCallback, checkAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
